const SEEN_IDS_PREFIX = 'saloon_online_booking_seen_ids_'
const LEGACY_SEEN_PREFIX = 'saloon_online_booking_seen_'

export const ONLINE_BOOKING_INBOX_EVENT = 'online-booking-inbox-changed'

/**
 * @param {number | null | undefined} branchId
 */
function seenIdsStorageKey(branchId) {
  return `${SEEN_IDS_PREFIX}${branchId}`
}

/**
 * @param {number | null | undefined} branchId
 * @returns {Set<number>}
 */
export function getSeenOnlineBookingIds(branchId) {
  if (!branchId) {
    return new Set()
  }

  try {
    const raw = localStorage.getItem(seenIdsStorageKey(branchId))
    if (!raw) {
      return new Set()
    }

    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return new Set()
    }

    return new Set(parsed.map((value) => Number(value)).filter((value) => Number.isFinite(value)))
  } catch {
    return new Set()
  }
}

/**
 * @param {number | null | undefined} branchId
 * @param {Set<number>} ids
 */
function saveSeenOnlineBookingIds(branchId, ids) {
  if (!branchId) {
    return
  }

  localStorage.setItem(seenIdsStorageKey(branchId), JSON.stringify([...ids]))
  window.dispatchEvent(new CustomEvent(ONLINE_BOOKING_INBOX_EVENT))
}

/**
 * Migrate timestamp-based seen state to per-booking ids.
 *
 * @param {number | null | undefined} branchId
 * @param {Array<{ id: number, created_at?: string | null }>} pending
 */
export function migrateLegacyOnlineBookingSeen(branchId, pending) {
  if (!branchId) {
    return
  }

  const legacyKey = `${LEGACY_SEEN_PREFIX}${branchId}`
  const legacySeenAt = localStorage.getItem(legacyKey)
  if (!legacySeenAt) {
    return
  }

  const ids = getSeenOnlineBookingIds(branchId)
  pending.forEach((item) => {
    if (item.created_at && item.created_at <= legacySeenAt) {
      ids.add(item.id)
    }
  })

  saveSeenOnlineBookingIds(branchId, ids)
  localStorage.removeItem(legacyKey)
}

/**
 * @param {number | null | undefined} branchId
 * @param {number} appointmentId
 */
export function markOnlineBookingSeen(branchId, appointmentId) {
  if (!branchId || !appointmentId) {
    return
  }

  const ids = getSeenOnlineBookingIds(branchId)
  ids.add(appointmentId)
  saveSeenOnlineBookingIds(branchId, ids)
}

/**
 * @param {number | null | undefined} branchId
 * @param {number[]} appointmentIds
 */
export function markOnlineBookingsSeen(branchId, appointmentIds) {
  if (!branchId || appointmentIds.length === 0) {
    return
  }

  const ids = getSeenOnlineBookingIds(branchId)
  appointmentIds.forEach((appointmentId) => ids.add(appointmentId))
  saveSeenOnlineBookingIds(branchId, ids)
}

/**
 * @param {Array<{ id: number, created_at?: string | null }>} pending
 * @param {number | null | undefined} branchId
 */
export function countUnreadOnlineBookings(pending, branchId) {
  if (!pending?.length || !branchId) {
    return 0
  }

  migrateLegacyOnlineBookingSeen(branchId, pending)

  const seenIds = getSeenOnlineBookingIds(branchId)
  return pending.filter((item) => !seenIds.has(item.id)).length
}

/**
 * @param {number} appointmentId
 * @param {Array<{ id: number }>} pending
 * @param {number | null | undefined} branchId
 */
export function isOnlineBookingUnread(appointmentId, pending, branchId) {
  if (!branchId) {
    return false
  }

  migrateLegacyOnlineBookingSeen(branchId, pending)

  const seenIds = getSeenOnlineBookingIds(branchId)
  return pending.some((item) => item.id === appointmentId) && !seenIds.has(appointmentId)
}

/**
 * @param {Array<{ id: number, created_at?: string | null }>} pending
 * @param {number | null | undefined} branchId
 */
export function getUnreadOnlineBookings(pending, branchId) {
  if (!pending?.length || !branchId) {
    return []
  }

  migrateLegacyOnlineBookingSeen(branchId, pending)

  const seenIds = getSeenOnlineBookingIds(branchId)
  return pending.filter((item) => !seenIds.has(item.id))
}
